import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { DayWindow, PlatformSummaryResponse } from "@starsach/shared-types";
import { useAccess, useDownload, usePlatformSummary, usePlatformTenants, usePlatformUsage, usePlatformUsers } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { Hint } from "../components/Hint";
import { Card, DataTable, DayWindowToggle, EmptyState, ErrorState, LoadingState, MutationError, PageHeader, RelativeTime, StatTile, StatusPill } from "../components/ui";
import { actionLabel } from "../lib/audit-labels";
import { StaffSection } from "./platform/StaffSection";

/**
 * The platform view (requirements.md section 12). A different question
 * from the Focus page: not "what is wrong inside a customer's estate" but
 * "who is using this, how much, and where is it refusing them".
 */
export function PlatformPage() {
  const { me } = useAuth();
  const [window, setWindow] = useState<DayWindow>(30);
  const summary = usePlatformSummary(window);
  const tenants = usePlatformTenants(window);
  const users = usePlatformUsers(window);
  const access = useAccess();

  if (me?.role !== "internal-team") {
    return <EmptyState title="Not available" hint="The platform view spans every tenant, so it is internal only." />;
  }
  if (summary.isPending) return <LoadingState label="Loading platform activity" />;
  if (summary.isError) return <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />;
  const s = summary.data;

  return (
    <>
      <PageHeader
        title="Platform"
        subtitle="Adoption and governance across every tenant"
        actions={<DayWindowToggle value={window} onChange={setWindow} />}
      />

      {/* The customer side first (the owner's ask of 2026-09-21): who is on the platform and whether their stacks are talking. */}
      <section aria-labelledby="platform-customers" className="mb-4">
        <h2 id="platform-customers" className="mb-2 text-sm font-medium text-neutral-600 dark:text-neutral-400">Customers<Hint id="platform.customers" label="how these are counted" /></h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <StatTile label="Organizations" value={s.customers.organisations} />
          <StatTile label="Environments" value={s.customers.environments} hint={<Sub>{s.customers.live} live</Sub>} />
          <StatTile label="Reporting" value={s.customers.reporting} tone={s.customers.noHeartbeat > 0 ? "warning" : "neutral"} hint={<Sub>{s.customers.noHeartbeat > 0 ? `${s.customers.noHeartbeat} with no heartbeat` : "every stack heartbeating"}</Sub>} />
          <StatTile label="Connect instances" value={s.customers.connectInstances} />
          <StatTile label="On current template" value={s.customers.onCurrentTemplate} tone={s.customers.behindTemplate > 0 ? "warning" : "neutral"} hint={s.customers.behindTemplate > 0 ? <Sub>{s.customers.behindTemplate} behind</Sub> : undefined} />
        </div>
      </section>
      <h2 className="mb-2 text-sm font-medium text-neutral-600 dark:text-neutral-400">Users and changes</h2>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={`Changes in ${window} days`} value={s.totals.changes} />
        <StatTile label="Signed in" value={s.users.signedIn} tone={s.users.signedIn === 0 ? "warning" : "neutral"} />
        <StatTile label="Denied" value={s.totals.denied} tone={s.totals.denied > 0 ? "critical" : "neutral"} />
        <StatTile label="Break glass" value={s.totals.breakGlass} tone={s.totals.breakGlass > 0 ? "warning" : "neutral"} />
      </div>

      {/*
        Section 12.1: signing in and making a change are different things,
        and a screen that merges them undercounts adoption in the
        flattering direction - a read-only user looks lapsed.
      */}
      <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">
        <strong>{s.users.signedIn}</strong> of {s.users.total} users signed in during this window;{" "}
        <strong>{s.users.madeAChange}</strong> changed something. Reading the portal writes no audit record, so the
        second figure is always the smaller one and is not a measure of use.
      </p>

      <ChangesChart summary={s} window={window} />

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="What changed">
          <DataTable
            caption={`Changes by action over the last ${window} days`}
            rows={s.changesByAction}
            rowKey={(r) => r.action}
            empty={<EmptyState title="Nothing changed in this window" />}
            columns={[
              { key: "action", header: "Action", render: (r) => <span title={r.action}>{actionLabel(r.action)}</span> },
              { key: "count", header: "Count", numeric: true, render: (r) => r.count },
            ]}
          />
        </Card>

        <Card title="Refusals">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-neutral-500">Denied</dt>
            <dd>{s.totals.denied} <span className="text-xs text-neutral-500">someone tried something they may not</span></dd>
            <dt className="text-neutral-500">Rejected</dt>
            <dd>{s.totals.rejected} <span className="text-xs text-neutral-500">a manager reviewed and said no</span></dd>
            <dt className="text-neutral-500">Break glass</dt>
            <dd>{s.totals.breakGlass} <span className="text-xs text-neutral-500">the documented bypass</span></dd>
          </dl>
          {s.approvalsAgeing.length > 0 && (
            <div role="alert" className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950">
              <p className="font-medium text-amber-900 dark:text-amber-100">
                {s.approvalsAgeing.length} change{s.approvalsAgeing.length === 1 ? "" : "s"} waiting more than {s.approvalsAgeingOverDays} days
              </p>
              <ul className="mt-1 list-disc pl-5 text-amber-900 dark:text-amber-100">
                {s.approvalsAgeing.slice(0, 5).map((a) => (
                  <li key={a.promotionId}>
                    {a.environmentId} - {a.ageDays} days, asked by {a.requestedBy}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-amber-900 dark:text-amber-100">A queue nobody works is how gating turns into a workaround.</p>
            </div>
          )}
        </Card>
      </div>

      <Card title="Tenants" className="mt-4">
        {tenants.isError && <ErrorState error={tenants.error} />}
        <DataTable
          caption={`Every tenant, by change volume over the last ${window} days`}
          rows={tenants.data?.tenants ?? []}
          rowKey={(t) => t.organisationId}

          sortBy={(t) => t.name}
          empty={<EmptyState title="No tenants yet" />}
          columns={[
            { key: "name", header: "Tenant", render: (t) => t.name },
            {
              key: "activity", header: "Activity",
              // Never active and gone quiet are different problems (12.4).
              render: (t) =>
                t.activity === "active" ? <StatusPill status="active" tone="ok" />
                : t.activity === "quiet" ? <StatusPill status={`quiet ${tenants.data?.quietAfterDays ?? 30}d+`} tone="warning" />
                : <StatusPill status="never active" tone="neutral" />,
            },
            { key: "envs", header: "Environments", numeric: true, render: (t) => t.environments },
            { key: "users", header: "Users", numeric: true, render: (t) => t.users },
            { key: "changes", header: "Changes", numeric: true, render: (t) => t.changes },
            { key: "denied", header: "Denied", numeric: true, render: (t) => t.denied },
            { key: "last", header: "Last change", render: (t) => <RelativeTime iso={t.lastChangeAt} /> },
          ]}
        />
      </Card>

      <Card title="Users" className="mt-4">
        {users.isError && <ErrorState error={users.error} />}
        <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-400">
          Stale means no session for {users.data?.staleAfterDays ?? s.staleAfterDays} days. What matters is the access a
          dormant account still holds, so it is listed beside it.
        </p>
        <DataTable
          caption={`Every portal user, those never seen or longest dormant first`}
          rows={users.data?.users ?? []}
          rowKey={(u) => `${u.organisationId}#${u.userId}`}

          sortBy={(u) => u.email ?? u.userId}
          empty={<EmptyState title="No users yet" />}
          columns={[
            { key: "who", header: "User", render: (u) => u.email ?? u.userId },
            { key: "tenant", header: "Tenant", render: (u) => u.organisationName },
            { key: "role", header: "Role", render: (u) => u.role },
            {
              key: "seen", header: "Last signed in",
              render: (u) =>
                !u.lastSeenAt ? <StatusPill status="never signed in" tone="warning" />
                : u.staleDays !== undefined ? <><StatusPill status={`stale ${u.staleDays}d`} tone="critical" /> <RelativeTime iso={u.lastSeenAt} /></>
                : <RelativeTime iso={u.lastSeenAt} />,
            },
            { key: "changed", header: "Last change", render: (u) => <RelativeTime iso={u.lastActionAt} /> },
            { key: "changes", header: "Changes", numeric: true, render: (u) => u.changes },
            {
              key: "access", header: "Still holds",
              // The point of listing this is to be able to act on it, so
              // each one links to where the role is taken away.
              render: (u) =>
                u.access.length === 0 ? (
                  <span className="text-neutral-500">no environment role</span>
                ) : (
                  <span className="flex flex-wrap gap-x-2">
                    {u.access.map((a) => (
                      <Link key={a.environmentId} to={`/access/${a.environmentId}`} className="underline">
                        {a.environmentId} ({a.role})
                      </Link>
                    ))}
                  </span>
                ),
            },
          ]}
        />
      </Card>

      <UsageBilling />
      <StaffSection staffRole={access.staffRole} />
    </>
  );
}

/**
 * #272: the invoice run. One row per organisation for a month - its live
 * environments' metered Connect usage, one reading per AWS account,
 * priced by the rule in config - and the CSV the invoice is raised from.
 * An organisation whose live environments reported nothing is said so,
 * never shown as zero.
 */
function UsageBilling() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const usage = usePlatformUsage(month);
  const download = useDownload();
  const money = (v: number) => v.toLocaleString(undefined, { style: "currency", currency: "USD" });
  const p = usage.data?.pricing;
  return (
    <Card title="Usage and billing" className="mt-4">
      <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-neutral-500">Month</span>
          <input type="month" className="rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900" value={month} aria-label="Billing month" onChange={(e) => e.target.value && setMonth(e.target.value)} />
        </label>
        {p && (
          <span className="text-xs text-neutral-500">
            {p.ratePerMinuteEquivalent.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 4 })}/min-eq · floor {money(p.monthlyFloor)} · cap {money(p.monthlyCap)} · chat ×{p.equivalents.chat}, email ×{p.equivalents.email}, case ×{p.equivalents.case}
          </span>
        )}
        <button
          type="button"
          className="ml-auto rounded-lg border border-neutral-300 px-3 py-1.5 text-sm dark:border-neutral-700"
          disabled={download.isPending}
          onClick={() => download.mutate(`/v1/platform/usage?month=${month}&format=csv`)}
        >
          {download.isPending ? "Preparing…" : "Download CSV for invoicing"}
        </button>
      </div>
      <MutationError of={download} className="mb-3" />
      {usage.isError && <ErrorState error={usage.error} />}
      <DataTable
        caption={`Usage and amount per organisation for ${month}`}
        rows={usage.data?.organisations ?? []}
        rowKey={(o) => o.organisationId}
        sortBy={(o) => o.name}
        filter={(usage.data?.organisations.length ?? 0) > 5}
        empty={<EmptyState title="No organisations" />}
        columns={[
          { key: "org", header: "Organization", render: (o) => <span className="font-medium">{o.name}</span> },
          {
            key: "envs", header: "Live environments reporting",
            render: (o) => {
              const live = o.environments.filter((e) => e.live);
              return live.length === 0 ? <span className="text-neutral-500">none live</span> : <span className="text-xs">{live.map((e) => `${e.name} (${e.reported ? (e.source === "cost-explorer" ? "bill" : "approx") : "not reported"})`).join(", ")}</span>;
            },
          },
          { key: "mineq", header: "Minute-eq", numeric: true, render: (o) => o.minute_equivalents.toLocaleString(undefined, { maximumFractionDigits: 0 }) },
          { key: "amount", header: "Amount", numeric: true, render: (o) => (o.unreported ? <StatusPill status="no usage reported" tone="warning" /> : <span className="font-medium tabular-nums">{money(o.amount)}</span>) },
        ]}
      />
    </Card>
  );
}

/** A tile's second line: a qualifier under the label, set apart from it (the walk of 2026-09-21 found "ENVIRONMENTS1 LIVE" run together). */
function Sub({ children }: { children: ReactNode }) {
  return <span className="ml-1.5 font-normal normal-case tracking-normal text-neutral-500">· {children}</span>;
}

/**
 * One series, so no legend - the title names it. The by-month totals sit
 * beside it as the tabular reading of the same data, which section 12.2
 * asks for in its own right.
 */
function ChangesChart({ summary, window }: { summary: PlatformSummaryResponse; window: DayWindow }) {
  return (
    <Card title={`Changes per day, last ${window} days`}>
      <div className="grid gap-4 lg:grid-cols-[3fr_1fr]">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={summary.changesByDay} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.15} vertical={false} />
              <XAxis
                dataKey="date"
                fontSize={11}
                tickFormatter={(d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString([], { day: "numeric", month: "short" })}
                minTickGap={24}
              />
              <YAxis width={32} fontSize={11} allowDecimals={false} />
              <Tooltip
                labelFormatter={(d) => new Date(`${String(d)}T00:00:00Z`).toLocaleDateString([], { dateStyle: "medium" })}
                formatter={(v) => [String(v), "changes"]}
              />
              <Bar dataKey="count" fill="var(--color-accent)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <DataTable
          caption="The same changes totalled by month"
          rows={summary.changesByMonth}
          rowKey={(m) => m.date}
          empty={<EmptyState title="No changes yet" />}
          columns={[
            { key: "month", header: "Month", render: (m) => new Date(`${m.date}-01T00:00:00Z`).toLocaleDateString([], { month: "short", year: "numeric" }) },
            { key: "count", header: "Changes", numeric: true, render: (m) => m.count },
          ]}
        />
      </div>
    </Card>
  );
}
